/**
 * FmMenu — portal dropdown menu for secondary commands. It replaces AntD
 * `Dropdown`/menu usage while keeping focus movement, Escape close, outside
 * click close, and controlled-open support inside the design system.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import {
  cx,
  menuMinWidth,
  isInteractiveKey,
  focusFirstItem,
  focusTrigger,
  useControllableOpen,
  useMenuPosition,
  useMenuLifecycle,
} from './FmMenuInternal';

const MENU_SEPARATOR_ROLE = 'separator' as const;

type TriggerProps = React.HTMLAttributes<HTMLElement> & {
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
  'aria-expanded'?: React.AriaAttributes['aria-expanded'];
};

export type FmMenuPlacement = 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';

interface FmMenuBaseItem {
  key: string;
  'data-testid'?: string;
}

export interface FmMenuCommandItem extends FmMenuBaseItem {
  type?: 'item';
  label: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
  /** The item names the page the owner is on (navigation menus). */
  current?: boolean;
  title?: string;
  onClick?: (event: FmMenuItemClickEvent) => void;
}

export interface FmMenuLabelItem extends FmMenuBaseItem {
  type: 'label';
  label: React.ReactNode;
}

export interface FmMenuSeparatorItem extends FmMenuBaseItem {
  type: 'separator';
}

export type FmMenuItem = FmMenuCommandItem | FmMenuLabelItem | FmMenuSeparatorItem;

export interface FmMenuItemClickEvent {
  key: string;
  item: FmMenuCommandItem;
  domEvent: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>;
}

export interface FmMenuProps {
  items: FmMenuItem[];
  /** Navigation menus open on hover; click and keyboard activation remain available. */
  trigger?: 'click' | 'hover';
  children: React.ReactElement<TriggerProps>;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onItemClick?: (event: FmMenuItemClickEvent) => void;
  placement?: FmMenuPlacement;
  minWidth?: number | string;
  focusFirstItemOnOpen?: boolean;
  /** Backward-compatible alias for focusFirstItemOnOpen. */
  autoFocus?: boolean;
  restoreFocusOnClose?: boolean;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  menuTestId?: string;
}

interface FmMenuSurfaceProps {
  items: FmMenuItem[];
  menuRef: React.RefObject<HTMLDivElement | null>;
  position: React.CSSProperties;
  minWidth?: number | string;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  menuTestId?: string;
  onClose: (restoreFocus?: boolean) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onSelect: (
    item: FmMenuCommandItem,
    domEvent: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
  ) => void;
}

function FmMenuSurface({
  items,
  menuRef,
  position,
  minWidth,
  className,
  menuTestId,
  onClose,
  onMouseEnter,
  onMouseLeave,
  onSelect,
}: FmMenuSurfaceProps): React.ReactElement {
  const handleContainerMouseLeave = () => {
    onClose(false);
  };

  const handleItemClick = (item: FmMenuCommandItem, e: React.MouseEvent<HTMLButtonElement>) => {
    onSelect(item, e);
  };

  const handleItemKeyDown = (
    item: FmMenuCommandItem,
    e: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(item, e);
    }
  };

  const menuContent = (
    <div
      ref={menuRef}
      role="menu"
      tabIndex={-1}
      className={cx('fm-menu__surface', className)}
      style={position}
      data-testid={menuTestId}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave ?? handleContainerMouseLeave}
    >
      {items.map((item, idx) => {
        if (item.type === 'separator') {
          return (
            <hr
              key={item.key || idx}
              className="fm-menu__separator"
              role={MENU_SEPARATOR_ROLE}
              aria-label="Menu separator"
              data-testid={item['data-testid']}
            />
          );
        }

        if (item.type === 'label') {
          return (
            <div
              key={item.key || idx}
              role="presentation"
              className="fm-menu__label"
              data-testid={item['data-testid']}
            >
              {item.label}
            </div>
          );
        }

        const commandItem = item as FmMenuCommandItem;
        return (
          <button
            key={commandItem.key}
            role="menuitem"
            disabled={commandItem.disabled}
            aria-disabled={commandItem.disabled || undefined}
            aria-current={commandItem.current ? 'page' : undefined}
            onClick={(e) => handleItemClick(commandItem, e)}
            onKeyDown={(e) => handleItemKeyDown(commandItem, e)}
            className={cx(
              'fm-menu__item',
              commandItem.disabled && 'fm-menu__item--disabled',
              commandItem.danger && 'fm-menu__item--danger',
              commandItem.current && 'fm-menu__item--current',
            )}
            title={commandItem.title}
            data-testid={commandItem['data-testid']}
            style={{ minWidth: menuMinWidth(minWidth) }}
          >
            {commandItem.icon && <span className="fm-menu__item-icon">{commandItem.icon}</span>}
            <span className="fm-menu__item-label">{commandItem.label}</span>
          </button>
        );
      })}
    </div>
  );

  return createPortal(menuContent, document.body);
}

/**
 * FmMenu — portal dropdown menu for secondary commands. It replaces AntD
 * `Dropdown`/menu usage while keeping focus movement, Escape close, outside
 * click close, and controlled-open support inside the design system.
 */
export const FmMenu: React.FC<FmMenuProps> = ({
  items,
  trigger: triggerMode = 'click',
  children,
  open,
  defaultOpen,
  onOpenChange,
  onItemClick,
  placement = 'bottomLeft',
  minWidth,
  focusFirstItemOnOpen = false,
  autoFocus = false,
  restoreFocusOnClose = true,
  className,
  menuTestId,
}) => {
  const triggerRootRef = React.useRef<HTMLSpanElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const hoverCloseTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Signals that the next open was keyboard-triggered and must land focus on the
  // first enabled menuitem regardless of the `focusFirstItemOnOpen` prop. A ref keeps this
  // per-open signal out of the render/effect dependency graph so it cannot
  // re-fire the lifecycle effect (which would risk the "Maximum update depth" loop).
  const focusFirstOnOpenRef = React.useRef(false);
  const [isOpen, setOpen, restoreFocusRef] = useControllableOpen({
    open,
    defaultOpen,
    onOpenChange,
  });
  const [position, updatePosition] = useMenuPosition({
    isOpen,
    placement,
    triggerRootRef,
    menuRef,
  });

  const cancelHoverClose = () => clearTimeout(hoverCloseTimer.current);
  const scheduleHoverClose = () => {
    cancelHoverClose();
    // Allow travel across the small gap between the trigger and its portal.
    hoverCloseTimer.current = setTimeout(() => setOpen(false, false), 150);
  };
  React.useEffect(() => () => clearTimeout(hoverCloseTimer.current), []);

  useMenuLifecycle({
    autoFocus: focusFirstItemOnOpen || autoFocus,
    focusFirstOnOpenRef,
    isOpen,
    menuRef,
    restoreFocusOnClose,
    restoreFocusRef,
    setOpen,
    triggerRootRef,
    updatePosition,
  });

  const handleTriggerClick: React.MouseEventHandler<HTMLElement> = (event) => {
    children.props.onClick?.(event);
    if (event.defaultPrevented) return;
    setOpen(triggerMode === 'hover' || !isOpen, !isOpen);
  };

  const handleTriggerKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
    children.props.onKeyDown?.(event);
    if (event.defaultPrevented || !isInteractiveKey(event.key)) return;
    event.preventDefault();
    cancelHoverClose();
    if (isOpen) {
      event.stopPropagation();
      focusFirstItem(menuRef);
      return;
    }
    // Keyboard-triggered open (Enter/Space/ArrowDown/ArrowUp on the trigger) must
    // move focus to the first enabled menuitem even when `autoFocus` is not set.
    focusFirstOnOpenRef.current = true;
    setOpen(true, true);
  };

  const selectItem = (
    item: FmMenuCommandItem,
    domEvent: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (item.disabled) return;
    const event = { key: item.key, item, domEvent };
    item.onClick?.(event);
    onItemClick?.(event);
    // Close and return focus to the trigger. `setOpen(false, false)` leaves the
    // open-time restoreFocusRef (the trigger) intact; the explicit focusTrigger
    // guarantees the return even for pointer activation where the clicked item
    // briefly held focus.
    setOpen(false, false);
    focusTrigger(triggerRootRef);
  };

  const trigger = React.cloneElement(children, {
    'aria-haspopup': 'menu',
    'aria-expanded': isOpen,
    onClick: handleTriggerClick,
    onKeyDown: handleTriggerKeyDown,
  });

  return (
    <>
      <span
        ref={triggerRootRef}
        className="fm-menu__trigger"
        onMouseEnter={
          triggerMode === 'hover'
            ? () => {
                cancelHoverClose();
                setOpen(true, false);
              }
            : undefined
        }
        onMouseLeave={triggerMode === 'hover' ? scheduleHoverClose : undefined}
      >
        {trigger}
      </span>
      {isOpen ? (
        <FmMenuSurface
          items={items}
          menuRef={menuRef}
          position={position}
          minWidth={minWidth}
          className={className}
          menuTestId={menuTestId}
          onClose={(restoreFocus = true) => setOpen(false, restoreFocus)}
          onMouseEnter={triggerMode === 'hover' ? cancelHoverClose : undefined}
          onMouseLeave={triggerMode === 'hover' ? scheduleHoverClose : undefined}
          onSelect={selectItem}
        />
      ) : null}
    </>
  );
};

export default FmMenu;
